/**
 * Wann eine Aufgabe fällig ist.
 *
 * Aus dem Kundentest (T-0022): „Eine Aufgabe hat ein Fälligkeitsdatum. Der
 * Zeitpunkt der Fälligkeit ist standardmäßig ausgeblendet und zum Ende des
 * Tages." Vorher stand dort ein ``datetime-local``-Feld, vorbelegt mit dem
 * Zeitpunkt des Öffnens — eine Aufgabe, die man morgens anlegte, war damit am
 * selben Morgen schon überfällig.
 */

/** Ohne ausdrückliche Uhrzeit gilt das Ende des Tages. */
export const TAGESENDE = '23:59';

/** Das heutige Datum als ``yyyy-mm-dd`` in der Zeitzone des Rechners. */
export function heuteIso(jetzt: Date = new Date()): string {
  const versatz = jetzt.getTimezoneOffset() * 60000;
  return new Date(jetzt.getTime() - versatz).toISOString().slice(0, 10);
}

/** Datum und (optionale) Uhrzeit zu dem Format, das die API erwartet. */
export function faelligkeitFuerApi(datum: string, uhrzeit: string): string {
  return datum ? `${datum} ${uhrzeit || TAGESENDE}:00` : '';
}

/** Datum und Uhrzeit einer Aufgabe; ``uhrzeit: null`` heißt „zum Ende des Tages“. */
export type Faelligkeit = { datum: string; uhrzeit: string | null };

/** Vorbelegung einer neuen Aufgabe: heute, zum Ende des Tages. */
export function faelligkeitHeute(jetzt: Date = new Date()): Faelligkeit {
  return { datum: heuteIso(jetzt), uhrzeit: null };
}

const MIT_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;
const zweistellig = (zahl: number) => String(zahl).padStart(2, '0');

/**
 * Einen gespeicherten Wert in die Felder zerlegen (T-0057) — ``2026-09-27T09:30``,
 * ``2026-09-27 09:30:00`` oder ``2026-09-27T09:30:00+00:00``.
 *
 * Ohne Zone ist es die Wandzeit, so wie das Formular sie schickt. Mit Zone kommt
 * der Wert vom Server, und der speichert seit T-0257 in UTC: 23:59 Uhr wird dort
 * zu 22:59 UTC. Abgelesen stand dann „22:59 Uhr“ an einer Aufgabe ohne Uhrzeit
 * (T-0299), und jedes Bearbeiten hätte sie um eine Stunde vorgezogen. Deshalb
 * wird in Ortszeit umgerechnet.
 *
 * Genau 00:00 UTC ist ein reines Datum: So stehen der Altbestand und
 * automatisch angelegte Aufgaben in der Datenbank — umgerechnet hieße das
 * „01:00 Uhr“. 23:59 Ortszeit gilt als Ende des Tages.
 */
export function faelligkeitAusWert(wert: string | null | undefined): Faelligkeit {
  const text = String(wert || '').trim();
  if (!text) return faelligkeitHeute();
  if (MIT_ZONE.test(text)) {
    const zeitpunkt = new Date(text);
    if (!Number.isNaN(zeitpunkt.getTime())) {
      if (zeitpunkt.getUTCHours() === 0 && zeitpunkt.getUTCMinutes() === 0) {
        return { datum: zeitpunkt.toISOString().slice(0, 10), uhrzeit: null };
      }
      const datum = `${zeitpunkt.getFullYear()}-${zweistellig(zeitpunkt.getMonth() + 1)}-${zweistellig(zeitpunkt.getDate())}`;
      const uhrzeit = `${zweistellig(zeitpunkt.getHours())}:${zweistellig(zeitpunkt.getMinutes())}`;
      return { datum, uhrzeit: uhrzeit === TAGESENDE ? null : uhrzeit };
    }
  }
  const datum = text.slice(0, 10);
  const uhrzeit = text.length >= 16 ? text.slice(11, 16) : '';
  return { datum, uhrzeit: !uhrzeit || uhrzeit === TAGESENDE ? null : uhrzeit };
}

/** Für die API: ``yyyy-mm-dd HH:mm:00``, ohne Uhrzeit zum Ende des Tages. */
export function faelligkeitWert(f: Faelligkeit): string {
  return faelligkeitFuerApi(f.datum, f.uhrzeit ?? '');
}

/**
 * Für Listen: „27.09.2026“ oder „27.09.2026, 09:30 Uhr“ (T-0299). Ohne
 * festgelegte Uhrzeit stand dort bisher „23:59 Uhr“ — das Ende des Tages ist
 * nur der gespeicherte Wert, keine Uhrzeit, die jemand eingetragen hat.
 */
export function faelligkeitAnzeige(wert: string | null | undefined): string {
  if (!String(wert || '').trim()) return '—';
  const f = faelligkeitAusWert(wert);
  const [jahr, monat, tag] = f.datum.split('-');
  const datum = `${tag}.${monat}.${jahr}`;
  return f.uhrzeit ? `${datum}, ${f.uhrzeit} Uhr` : datum;
}

/** Für Zusammenfassungen: „27.09.2026“ bzw. „27.09.2026, 09:30 Uhr“. */
export function faelligkeitText(f: Faelligkeit): string {
  if (!f.datum) return '—';
  const [jahr, monat, tag] = f.datum.split('-');
  const datum = `${tag}.${monat}.${jahr}`;
  return f.uhrzeit ? `${datum}, ${f.uhrzeit} Uhr` : `${datum} (Ende des Tages)`;
}
